// Maps a captured lead + extraction snapshot to the salesIntake prefill contract.

import type { Lead, ExtractedLeadV2 } from "@/types/lead";

import type { LeadIntakePrefillV1 } from "./leadIntakePrefill";

function leadCustomerName(lead: Lead, e: ExtractedLeadV2): string {
  const parts = [e.first_name, e.last_name]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean);
  if (parts.length) return parts.join(" ");
  return (lead.name || "").trim();
}

function leadInstallAddress(e: ExtractedLeadV2): string {
  const full = (e.install_address_full || "").trim();
  if (full) return full;
  const parts = [
    e.install_address_street,
    e.install_address_city,
    e.install_address_state,
    e.install_address_postal_code,
  ]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean);
  if (parts.length) return parts.join(", ");
  return (e.customer_city_state || "").trim();
}

export function buildIntakeNotesFromLead(e: ExtractedLeadV2): string {
  const lines: string[] = [];
  const extractedNotes = (e.notes || "").trim();
  if (extractedNotes) lines.push(extractedNotes);

  const productParts = [e.product_line, e.product_subtype, e.size_config]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean);
  if (productParts.length) lines.push(`Product: ${productParts.join(" · ")}`);

  if (e.product_interest_category?.trim()) {
    lines.push(`Interest category: ${e.product_interest_category.trim()}`);
  }
  if (Array.isArray(e.size_options) && e.size_options.length) {
    lines.push(`Size options: ${e.size_options.join(", ")}`);
  }
  if (e.enunciated_budget?.trim()) lines.push(`Budget: ${e.enunciated_budget.trim()}`);
  if (e.interest_level) lines.push(`Interest level: ${e.interest_level}`);
  if (e.ready_to_buy_timeline) lines.push(`Timeline: ${e.ready_to_buy_timeline}`);
  if (e.lead_source_detail?.trim()) {
    lines.push(`Lead source detail: ${e.lead_source_detail.trim()}`);
  }
  if (Array.isArray(e.interest_tags) && e.interest_tags.length) {
    lines.push(`Tags: ${e.interest_tags.join(", ")}`);
  }

  return lines.join("\n");
}

export function leadToIntakePrefill(
  lead: Lead,
  workspaceLocationId?: string | null,
): LeadIntakePrefillV1 | null {
  const e = lead.extracted;
  if (!e) return null;

  const linkedOpportunityId = (lead.linked_opportunity_id ?? "").trim() || null;

  return {
    v: 1,
    lead_id: lead.id,
    linked_opportunity_id: linkedOpportunityId,
    seller_location_id:
      (lead.origin_location_id || "").trim() ||
      (workspaceLocationId || "").trim() ||
      null,
    customer: {
      name: leadCustomerName(lead, e),
      phone: (e.phone || "").trim(),
      email: (e.email || "").trim(),
      address: leadInstallAddress(e),
    },
    notes: buildIntakeNotesFromLead(e),
  };
}
